/**
 * Concept2 ergometer math. Every formula here is the one Concept2 publishes,
 * so results match what a PM5 shows.
 *
 * Pace <-> watts (https://www.concept2.com/indoor-rowers/training/calculators/watts-calculator):
 *   watts = 2.80 / pace^3, where pace is seconds per meter.
 *   Check: a 2:00/500m split is 0.24 s/m -> 2.80 / 0.24^3 = 202.5 W (PM5 shows 203).
 *
 * Calories (Concept2 PM monitor manual, "Using the Display Options"):
 *   Cal/hour = (4 x watts / 1.1639) + 300
 *   This assumes a 175 lb (79.5 kg) rower. Concept2's calorie calculator
 *   (https://www.concept2.com/indoor-rowers/training/calculators/calorie-calculator)
 *   adjusts it for body weight:
 *   true Cal/hour = PM Cal/hour - 300 + 1.714 x weight_lb
 */

export const WATTS_CONSTANT = 2.8;

/** Watts for a pace given in seconds per 500 m. */
export function wattsFromSplit(splitSec: number): number {
  const pacePerMeter = splitSec / 500;
  return WATTS_CONSTANT / pacePerMeter ** 3;
}

/** Seconds per 500 m for a given power in watts. */
export function splitFromWatts(watts: number): number {
  return Math.cbrt(WATTS_CONSTANT / watts) * 500;
}

/** Calories per hour as the PM displays them (175 lb reference rower). */
export function pmCaloriesPerHour(watts: number): number {
  return (4 * watts) / 1.1639 + 300;
}

/** Concept2's body-weight adjustment of the PM calorie rate. */
export function adjustedCaloriesPerHour(watts: number, weightLb: number): number {
  return pmCaloriesPerHour(watts) - 300 + 1.714 * weightLb;
}

/** Total seconds to row `meters` at a split given in seconds per 500 m. */
export function timeForDistance(splitSec: number, meters: number): number {
  return (splitSec / 500) * meters;
}

/** Parses "1:52.3", "112.3" or "1:52" into seconds. Returns NaN when invalid. */
export function parseTime(input: string): number {
  const s = input.trim();
  if (!s) return NaN;
  const parts = s.split(":");
  if (parts.length > 3 || parts.some((p) => p === "" || !/^\d+(\.\d+)?$/.test(p))) return NaN;
  return parts.reduce((total, p) => total * 60 + parseFloat(p), 0);
}

/** Formats seconds as m:ss.t (or h:mm:ss.t past an hour). */
export function formatTime(totalSec: number): string {
  if (!Number.isFinite(totalSec) || totalSec < 0) return "—";
  const tenths = Math.round(totalSec * 10);
  const h = Math.floor(tenths / 36000);
  const m = Math.floor((tenths % 36000) / 600);
  const s = (tenths % 600) / 10;
  const ss = s.toFixed(1).padStart(4, "0");
  return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${ss}` : `${m}:${ss}`;
}

/**
 * Training zones as a share of 2K average power, using the five UT2 / UT1 /
 * AT / TR / AN bands common in rowing programmes. The percentages are a
 * coaching convention, not a Concept2 standard, and coaches draw the
 * boundaries slightly differently, so the page presents them as starting points.
 */
export const TRAINING_ZONES = [
  { code: "UT2", name: "Utilization 2 (easy aerobic)", min: 0.45, max: 0.6, use: "Long steady pieces, 30-90 minutes. Most of your weekly volume." },
  { code: "UT1", name: "Utilization 1 (hard aerobic)", min: 0.6, max: 0.7, use: "Steady pieces of 20-40 minutes. Conversational pace is gone." },
  { code: "AT", name: "Anaerobic threshold", min: 0.7, max: 0.8, use: "Threshold intervals such as 3-4 x 8 minutes with short rest." },
  { code: "TR", name: "Transport (race-pace work)", min: 0.8, max: 1.05, use: "2K-pace intervals such as 6-8 x 500 m or 4 x 1,000 m." },
  { code: "AN", name: "Anaerobic (sprints)", min: 1.05, max: 1.15, use: "Short, very hard efforts of 250 m or less with full recovery." },
] as const;
